import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useLocation } from "react-router";
import { useHistory } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { StickyColumnWithEntityStatus } from "go-core/components/StickyColumnWithEntityStatus";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig, ListParamsType, MultipleActionsParams } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { UpsellApi, UpsellVisibilityType } from "../../../../../../../../../../services/Api/Organization/types";
import { convertListStatusToEntityStatus } from "../../../../../../../../../../utils/entityStatus/entityStatus";

interface ListState {
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
}

const listName = "UPSELL";
const resourceType = "UPSELL";

const List = ({ resource, mobileActions }: ListState) => {
	const segmentContext = useContext(SegmentContext);
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const location = useLocation();
	const { addSuccessFlash } = useFlash();
	const history = useHistory();

	if (!resource) return null;
	const data = resource.read();

	const removeUpsell = async (item: UpsellApi) => {
		await apiOrganization.removeUpsell(item.id!);
		addSuccessFlash(t("lib:common.flash.removed"));
	};

	const restoreUpsell = async (item: UpsellApi) => {
		await apiOrganization.restoreUpsell(item.id!);
		addSuccessFlash(t("lib:common.flash.restored"));
	};

	const disableUpsell = async (item: UpsellApi) => {
		await apiOrganization.disableUpsell(item.id!);
		addSuccessFlash(t("lib:common.word.completed"));
	};

	const removeUpsells = async (
		multipleActionsItems: UpsellApi[],
		_?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id!.toString()).join(",");
		await apiOrganization.removeUpsells(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("lib:common.flash.removed"));
	};

	const restoreUpsells = async (
		multipleActionsItems: UpsellApi[],
		_?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id!.toString()).join(",");
		await apiOrganization.restoreUpsells(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("lib:common.flash.restored"));
	};

	const disableUpsells = async (
		multipleActionsItems: UpsellApi[],
		_?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id!.toString()).join(",");
		await apiOrganization.disableUpsells(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	let config: ListConfig = {
		fields: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
				render: (item: UpsellApi) => {
					return (
						<StickyColumnWithEntityStatus
							status={convertListStatusToEntityStatus(item.status)}
							minWidth={200}
							to={`${location.pathname}/${item.id}`}
						>
							{item.name}
						</StickyColumnWithEntityStatus>
					);
				},
				renderExport: (item: UpsellApi) => item.name,
			},
			{
				id: "label",
				name: t("modules.upsell.field.label.title"),
				type: "text",
			},
			{
				id: "priority",
				name: t("modules.upsell.field.priority.title"),
				type: "number",
			},
			{
				id: "visibility_type",
				name: t("modules.upsell.field.visibility_type.title"),
				type: "list",
				listOptions: {
					[UpsellVisibilityType.ALL]: t("enums.upsell.visibility_type.ALL"),
					[UpsellVisibilityType.MISSING]: t("enums.upsell.visibility_type.MISSING"),
					[UpsellVisibilityType.NONE_IF_ANY_INCLUDED]: t("enums.upsell.visibility_type.NONE_IF_ANY_INCLUDED"),
				},
				render: (item: UpsellApi) => t(`enums.upsell.visibility_type.${item.visibility_type}`),
			},
		],
		filters: [
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				listOptions: {
					DISABLED: t("lib:enums.common.status.DISABLED"),
					DELETED: t("lib:enums.common.status.DELETED"),
					ENABLED: t("lib:enums.common.status.ENABLED"),
				},
			},
		],
		actions: [
			{
				name: t("lib:common.action.edit"),
				link: (item: UpsellApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("lib:common.action.remove"),
				click: (item: UpsellApi) => removeUpsell(item),
				visible: (item: UpsellApi) => item.status !== "DELETED",
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("lib:confirmation.message.remove"),
				},
			},
			{
				name: t("lib:common.action.activate"),
				click: (item: UpsellApi) => restoreUpsell(item),
				visible: (item: UpsellApi) => item.status === "DELETED" || item.status === "DISABLED",
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("lib:confirmation.message.activate"),
				},
			},
			{
				name: t("lib:common.action.disable"),
				click: (item: UpsellApi) => disableUpsell(item),
				visible: (item: UpsellApi) => item.status === "ENABLED",
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("lib:confirmation.message.disable"),
				},
			},
			{
				name: t("lib:common.action.copy"),
				click: (item: UpsellApi) => {
					history.push({
						pathname: `${location.pathname}/new`,
						state: {
							upsell: {
								...item,
								id: null,
								name: `${t("common.word.copy")} ${item.name}`,
							},
						},
					});
				},
			},
		],
		multipleActions: [
			{
				name: t("lib:common.action.remove"),
				click: async (
					multipleActionsItems: UpsellApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => removeUpsells(multipleActionsItems, listParams, areAllItemsSelected, multipleActionParams),
				refreshListAfter: true,
				visible: (multipleActionsItems: UpsellApi[]) => {
					return multipleActionsItems.filter((item) => item.status !== "DELETED").length > 0;
				},
				confirmation: {
					use: true,
					message: t("lib:confirmation.message.remove"),
				},
			},
			{
				name: t("lib:common.action.activate"),
				click: async (
					multipleActionsItems: UpsellApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => restoreUpsells(multipleActionsItems, listParams, areAllItemsSelected, multipleActionParams),
				refreshListAfter: true,
				visible: (multipleActionsItems: UpsellApi[]) => {
					return (
						multipleActionsItems.filter((item) => item.status === "DELETED" || item.status === "DISABLED")
							.length > 0
					);
				},
				confirmation: {
					use: true,
					message: t("lib:confirmation.message.activate"),
				},
			},
			{
				name: t("lib:common.action.disable"),
				click: async (
					multipleActionsItems: UpsellApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => disableUpsells(multipleActionsItems, listParams, areAllItemsSelected, multipleActionParams),
				refreshListAfter: true,
				visible: (multipleActionsItems: UpsellApi[]) => {
					return multipleActionsItems.filter((item) => item.status === "ENABLED").length > 0;
				},
				confirmation: {
					use: true,
					message: t("lib:confirmation.message.disable"),
				},
			},
		],
		selectedColumns: ["name", "label", "priority", "visibility_type"],
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "expressions,expressions.conditions,entities,entities.entity";
			return apiOrganization.getUpsells(params, { cancelToken: sourceToken?.token });
		},
		segments: [
			{
				id: "all",
				name: t("lib:common.word.all"),
				slug: "all",
			},
			{
				id: "deleted",
				name: t("lib:go_list.filters.deleted"),
				slug: "deleted",
				filters: [
					{
						filterId: "status",
						value: "DELETED",
						condition: "e",
					},
				],
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: t("modules.upsell.field.export_config.title"),
			filename: t("modules.upsell.header.title"),
			organization: `${organization.name}`,
			taxIdNo: organization?.more?.print_company_on_pdf ? organization.more?.company_tax_id_no : undefined,
			company: organization?.more?.print_company_on_pdf ? organization.more?.company_name : undefined,
			pdfOrientation: organization?.more?.pdf_orientation,
			pdfFontSize: organization?.more?.default_pdf_font_size?.toString(),
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		doesIdColumnRedirectToPreviewPage: true,
		numberOfStickyColumnsAtTheStart: 1,
		fetchTotalItemsCountForSelectedFilters: (
			fetchTotalItemsParams: Record<string, any>,
			sourceToken?: CancelTokenSource
		) => apiOrganization.getUpsellsCount(fetchTotalItemsParams, { cancelToken: sourceToken?.token }),
	};
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	return (
		<ListData
			config={config}
			emptyList={{
				addAction: () => history.push(`${location.pathname}/new`),
			}}
			canManageMultiActionsForAllItems
			mobileActions={mobileActions}
		/>
	);
};

const OrganizationMenuUpsellsIndexPage = (props: RouteComponentProps) => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const buttons: ButtonProps[] = [
		{
			title: t("lib:common.action.add"),
			path: `${props.match.url}/new`,
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("lib:common.action.add"),
			path: `${props.match.url}/new`,
		},
	];

	useEffect(() => {
		handleChangeTabTitle(t("modules.upsell.header.title"));
		setResource(wrapPromise(segmentContext.get(listName, resourceType)));
	}, []);

	return (
		<>
			{!isMobile && <Header title={t("modules.upsell.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} mobileActions={mobileActions} />
			</Suspense>
		</>
	);
};

export default OrganizationMenuUpsellsIndexPage;
